import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  getAdHocSummary,
  getAdHocSearchVolume,
  getAdHocUniqueUsers,
  getAdHocHeatmap,
  getAdHocResultsQuality,
} from "../../../../util/adHocAnalyticsApi";
import MetricsCards from "./MetricsCards";
import SearchVolumeChart from "./SearchVolumeChart";
import GeographicHeatmap from "./GeographicHeatmap";

const Section = styled.div`
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const SectionTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 2px solid #4caf50;
  padding-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 1.2em;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const StatusBar = styled.div`
  display: flex;
  width: 100%;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ddd;
`;

const StatusSegment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => props.color};
  width: ${(props) => props.width}%;
  min-width: ${(props) => (props.width > 0 ? "40px" : "0")};
  transition: width 0.3s;
`;

const StatusLegend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #555;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: ${(props) => props.color};
  }
`;

const STATUS_COLORS = {
  completed: "#4caf50",
  failed: "#e53935",
  cancelled: "#9e9e9e",
  processing: "#ff9800",
  pending: "#90a4ae",
};

const STATUS_LABELS = {
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
  processing: "Processing",
  pending: "Pending",
};

const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

const formatPercent = (v) => `${v.toFixed(1)}%`;

const AdHocAnalyticsSection = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [searchVolume, setSearchVolume] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [resultsQuality, setResultsQuality] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          summaryResult,
          volumeResult,
          usersResult,
          heatmapResult,
          qualityResult,
        ] = await Promise.all([
          getAdHocSummary(filters),
          getAdHocSearchVolume(filters),
          getAdHocUniqueUsers(filters),
          getAdHocHeatmap(filters),
          getAdHocResultsQuality(filters),
        ]);
        if (cancelled) return;
        setSummary(summaryResult);
        setSearchVolume(volumeResult);
        setUniqueUsers(usersResult);
        setHeatmap(heatmapResult);
        setResultsQuality(qualityResult);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading ad hoc analytics:", err);
        setError("Failed to load Discovery Search analytics. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filters.startDate, filters.endDate]);

  if (loading && !summary) {
    return <LoadingMessage>Loading Discovery Search analytics...</LoadingMessage>;
  }

  if (error) return <ErrorMessage>{error}</ErrorMessage>;
  if (!summary) return null;

  const kpiCards = [
    { title: "Total Searches", value: summary.totalSearches.toLocaleString() },
    { title: "Unique Users", value: summary.uniqueUsers.toLocaleString() },
    { title: "Completion Rate", value: formatPercent(summary.completionRate) },
    { title: "Avg Duration", value: formatDuration(summary.avgDurationSeconds) },
    { title: "Avg Vegan / Search", value: summary.avgVeganRestaurants.toFixed(1) },
    { title: "Vegan Hit Rate", value: formatPercent(summary.hitRate) },
  ];

  const statusTotal = Object.values(summary.statusBreakdown).reduce(
    (a, b) => a + b,
    0
  );
  const statusOrder = ["completed", "failed", "cancelled", "processing", "pending"];

  return (
    <>
      <MetricsCards cards={kpiCards} />

      <Section>
        <SectionTitle>Search Outcome Breakdown</SectionTitle>
        {statusTotal > 0 ? (
          <>
            <StatusBar>
              {statusOrder.map((status) => {
                const count = summary.statusBreakdown[status];
                const width = (count / statusTotal) * 100;
                if (width === 0) return null;
                return (
                  <StatusSegment
                    key={status}
                    color={STATUS_COLORS[status]}
                    width={width}
                    title={`${STATUS_LABELS[status]}: ${count} (${width.toFixed(1)}%)`}
                  >
                    {width >= 6 ? `${width.toFixed(0)}%` : ""}
                  </StatusSegment>
                );
              })}
            </StatusBar>
            <StatusLegend>
              {statusOrder.map((status) => (
                <LegendItem key={status} color={STATUS_COLORS[status]}>
                  {STATUS_LABELS[status]}: {summary.statusBreakdown[status]}
                </LegendItem>
              ))}
            </StatusLegend>
          </>
        ) : (
          <LoadingMessage>No searches in this date range.</LoadingMessage>
        )}
      </Section>

      <Section>
        <SectionTitle>Where Users Invoke Discovery Search</SectionTitle>
        <GeographicHeatmap data={heatmap} loading={loading} />
      </Section>

      <TwoColumnGrid>
        <Section>
          <SectionTitle>Searches Over Time</SectionTitle>
          <SearchVolumeChart data={searchVolume} loading={loading} />
        </Section>
        <Section>
          <SectionTitle>Unique Users Over Time</SectionTitle>
          <SearchVolumeChart data={uniqueUsers} loading={loading} />
        </Section>
      </TwoColumnGrid>

      <Section>
        <SectionTitle>Vegan Hit Rate Over Time</SectionTitle>
        <SearchVolumeChart
          data={resultsQuality}
          loading={loading}
          valueFormatter={formatPercent}
        />
      </Section>
    </>
  );
};

export default AdHocAnalyticsSection;
